export type TeamMemberLike = {
    id?: string;
    name?: string;
    email?: string;
    role?: string;
    category?: string;
    [key: string]: any;
};

export const isTeamMemberIdValue = (value: string | null | undefined): boolean => {
    if (!value) return false;
    return /^id_mgu/i.test(String(value).trim());
};

export const mergeTeamPool = (
    team: TeamMemberLike[] = [],
    teamRegistry: TeamMemberLike[] = []
): TeamMemberLike[] => {
    // Deduplizierung primär über E-Mail (case-insensitive, getrimmt).
    // 'team' kommt zuerst -> Admin-Konfiguration gewinnt gegen Registry-Dubletten.
    const seenEmails = new Set<string>();
    const seenIds = new Set<string>();
    const pool: TeamMemberLike[] = [];
    [...(team || []), ...(teamRegistry || [])].forEach((member) => {
        if (!member) return;
        const memberEmail = member.email ? String(member.email).trim().toLowerCase() : '';
        const memberId = member.id ? String(member.id).trim().toLowerCase() : '';

        if (memberEmail) {
            if (seenEmails.has(memberEmail)) return;
            seenEmails.add(memberEmail);
            if (memberId) seenIds.add(memberId);
        } else if (memberId) {
            // Fallback für Einträge ohne E-Mail: bestehende ID-Deduplizierung
            if (seenIds.has(memberId)) return;
            seenIds.add(memberId);
        }
        pool.push(member);
    });
    return pool;
};

export const isCoachLikeMember = (member: TeamMemberLike | null | undefined): boolean => {
    if (!member) return false;
    const category = String(member.category || '').toLowerCase();
    const role = String(member.role || '').toLowerCase();
    return category === 'coach' || category === 'support' || role === 'coach' || role === 'support';
};

export const getAssignableTeam = (team: TeamMemberLike[] = []): TeamMemberLike[] => {
    return (team || []).filter((member) => {
        if (!member) return false;
        const category = String(member.category || '').toLowerCase();
        const role = String(member.role || '').toLowerCase();
        // Support (Omar): gleiche Portal-Rechte wie Coach, aber nicht zuordenbar
        if (category === 'support' || role === 'support') return false;
        return category === 'coach' || role === 'coach';
    });
};

export const resolveMemberById = (
    id: string | null | undefined,
    team: TeamMemberLike[] = []
): TeamMemberLike | null => {
    if (!id) return null;
    const normalized = String(id).trim().toLowerCase();
    return (team || []).find(
        (member) => member?.id && String(member.id).trim().toLowerCase() === normalized
    ) || null;
};

const legacyMemberMatches = (normalized: string, member: TeamMemberLike): boolean => {
    if (member.email) {
        const memberEmail = String(member.email).trim().toLowerCase();
        if (memberEmail && normalized === memberEmail) return true;

        const memberLocal = memberEmail.split('@')[0] || '';
        const valueLocal = normalized.includes('@') ? normalized.split('@')[0] : normalized;
        if (memberLocal && valueLocal) {
            if (memberLocal === valueLocal) return true;
            if (memberLocal.includes(valueLocal) || valueLocal.includes(memberLocal)) return true;
        }
    }

    if (member.name) {
        const name = String(member.name).trim().toLowerCase();
        const firstName = name.split(' ')[0] || '';
        if (normalized === name) return true;
        if (normalized.includes(name) || name.includes(normalized)) return true;
        if (firstName.length > 2 && (normalized.includes(firstName) || firstName.includes(normalized))) return true;
    }

    return false;
};

export const resolveMemberByBetreuer = (
    value: string | null | undefined,
    team: TeamMemberLike[] = [],
    teamRegistry: TeamMemberLike[] = []
): TeamMemberLike | null => {
    if (!value) return null;
    const normalized = String(value).trim().toLowerCase();
    if (!normalized || normalized === 'unzugewiesen' || normalized === 'undefined' || normalized === 'null') {
        return null;
    }

    const pool = mergeTeamPool(team, teamRegistry);

    if (isTeamMemberIdValue(value)) {
        const byId = resolveMemberById(value, pool);
        if (byId) return byId;
    }

    const legacyMatch = pool.find((member) => legacyMemberMatches(normalized, member));
    if (legacyMatch) return legacyMatch;

    return null;
};

export const betreuerMatchesMember = (
    betreuerValue: string | null | undefined,
    member: TeamMemberLike | null | undefined,
    team: TeamMemberLike[] = [],
    teamRegistry: TeamMemberLike[] = []
): boolean => {
    if (!member?.id) return false;
    const resolved = resolveMemberByBetreuer(betreuerValue, team, teamRegistry);
    if (resolved?.id) {
        // Case- und Whitespace-unabhängiger ID-Vergleich (fängt DB-Abweichungen ab)
        return String(resolved.id).trim().toLowerCase() === String(member.id).trim().toLowerCase();
    }
    // Fallback: Wert direkt gegen das Mitglied prüfen — greift, wenn das Mitglied
    // nicht im Team-Pool steht (z. B. Inhaber-Profil) oder der Pool noch leer ist
    const normalized = String(betreuerValue || '').trim().toLowerCase();
    if (!normalized) return false;
    if (normalized === String(member.id).trim().toLowerCase()) return true;
    return legacyMemberMatches(normalized, member);
};

export const collectBetreuerValues = (client: any): string[] => {
    return [
        client?.betreuer,
        client?.coachId,
        client?.coach,
        client?.clientData?.betreuer,
        client?.clientData?.coachId,
        client?.clientData?.coach,
    ]
        .filter(Boolean)
        .map((value) => String(value).trim());
};

export const resolveClientCoach = (
    client: any,
    team: TeamMemberLike[] = [],
    teamRegistry: TeamMemberLike[] = []
): TeamMemberLike | null => {
    const candidates = collectBetreuerValues(client);
    for (const value of candidates) {
        const resolved = resolveMemberByBetreuer(value, team, teamRegistry);
        if (resolved) return resolved;
    }
    return null;
};

export const clientMatchesMember = (
    client: any,
    member: TeamMemberLike | null | undefined,
    team: TeamMemberLike[] = [],
    teamRegistry: TeamMemberLike[] = []
): boolean => {
    if (!member?.id) return false;
    return collectBetreuerValues(client).some((value) => betreuerMatchesMember(value, member, team, teamRegistry));
};
